<template>
  <div class="dashboard">
    <div class="dashboard__content">
    <!-- Page Header -->
    <div class="dashboard__header">
      <div>
        <h3 class="mb-4 mt-1">Oversikt</h3>
      </div>
      <div class="dashboard__header-right">
        <!-- Location Selector (only for multi-location merchants) -->
        <div v-if="hasMultiLocations" class="dashboard__location-selector">
          <select v-model="selectedLocationId" @change="onLocationChange" class="dashboard__select">
            <option value="ALL">Alle avdelinger</option>
            <option v-for="location in locations" :key="location.id" :value="location.id">
              {{ location.name }}
            </option>
          </select>
        </div>
        <div class="dashboard__date-selector">
          <div class="dashboard__date-input">
            <font-awesome-icon icon="calendar-alt" class="dashboard__date-icon" />
            <datepicker v-model="selectedDateObj" :locale="nbLocale" inputFormat="dd. MMM yyyy" :upperLimit="new Date()" @update:modelValue="onDateChange" />
          </div>
        </div>
      </div>
    </div>

    <!-- Billing: trial / payment status nudge (subscription slices 2 & 4) -->
    <div v-if="showBillingBanner" class="billing-banner" :class="billingBannerClass">
      <div class="billing-banner__text">
        <template v-if="billingSuspended">
          <strong>{{ billingStatus.status === 'expired' ? 'Prøveperioden er utløpt.' : 'Abonnementet er satt på pause.' }}</strong>
          Legg til eller oppdater betalingskortet for å gjenoppta tjenesten.
        </template>
        <template v-else-if="billingPastDue">
          <strong>Betaling feilet.</strong>
          Oppdater betalingskortet for å unngå at tjenesten settes på pause.
        </template>
        <template v-else>
          <strong>Prøveperiode — {{ billingDaysLeft }} {{ billingDaysLeft === 1 ? 'dag' : 'dager' }} igjen.</strong>
          Legg til et betalingskort for å unngå avbrudd i tjenesten.
        </template>
      </div>
      <a href="/billing" class="bo_btn billing-banner__btn">{{ (billingSuspended || billingPastDue) ? 'Oppdater betalingskort' : 'Legg til betalingskort' }}</a>
    </div>

    <!--
      The setup nudge. Two states, and the split is the point: the homepage is free and finishing it is
      always worth a nudge; taking orders online is a paid upgrade and a merchant who has not asked for
      it is not behind on anything. Hardware is not here at all — it costs money, and a standing banner
      is not where we ask somebody to spend it.
    -->
    <div v-if="showSiteSetupBanner" class="site-setup-banner">
      <div class="site-setup-banner__text">
        <template v-if="!obSite">
          <strong>Hjemmesiden din er nesten klar.</strong>
          Vi har allerede laget en med navnet, adressen og åpningstidene deres. Bytt bildet, skriv noen
          ord om stedet, og publiser. Den koster ingenting.
          <p class="site-setup-banner__note">
            Vil du også ta imot bestillinger på nett? Det er to steg til — når du vil:
          </p>
        </template>
        <template v-else>
          <strong>Nesten klar til å ta imot bestillinger.</strong>
          Hjemmesiden din er publisert. Dette står igjen før gjestene kan bestille og betale på nett:
        </template>

        <ul class="site-setup-banner__steps">
          <li v-for="step in onboardingSteps" :key="step.key" :class="{ done: step.done }">
            <span class="site-setup-step__check">{{ step.done ? '✓' : '○' }}</span>
            <span>{{ step.before }}<a v-if="step.link" :href="step.link">{{ step.linkText }}</a>{{ step.after }}</span>
          </li>
        </ul>
        <!--
          Two sentences, and the first one is the one that matters. «De gratis dagene» sitting directly
          under a list of ordering steps reads as if the homepage were on a clock too — so the homepage
          is named as free BEFORE the trial is mentioned at all. A merchant who thinks a countdown is
          running rushes the menu, and worse, will not build anything on a platform they think is
          about to start charging them.
        -->
        <p class="site-setup-banner__trial">
          Hjemmesiden er gratis for alltid — den har ingen prøveperiode.
          <template v-if="trialNotStarted">
            De 30 gratis dagene gjelder bestillingsmodulene, og begynner å telle først den dagen du kan
            ta imot bestillinger. Ta den tiden du trenger.
          </template>
        </p>
      </div>
      <a :href="setupCtaLink" class="bo_btn site-setup-banner__btn">{{ setupCtaLabel }}</a>
    </div>

    <!-- Control Panel Section (write quick-actions; hidden for the read-only ACCOUNTANT role) -->
    <div v-if="!isAccountant" class="dashboard__section">
      <InlineSpinner v-if="loadingBasicData" />
      <ControlPanel
        v-else
        :locations="locations"
        :closingDaysArray="closingDaysArray"
        :categories="categories"
        :soldOutItems="soldOutItems"
      />
    </div>

    <!-- KPI Cards -->
    <div class="dashboard__kpis">
      <KpiCard
        :value="totalSales"
        :label="salesLabel"
        :subtitle="salesSubtitle"
        icon="coins"
        iconBgColor="#B30E10"
        format="currency"
        :trend="salesTrend"
        :loading="loadingTodaySales"
      />
      <KpiCard
        :value="ordersCount"
        label="Antall ordrer"
        :subtitle="dateSubtitle"
        icon="shopping-bag"
        iconBgColor="#4ECDC4"
        format="number"
        :trend="ordersTrend"
        :loading="loadingTodaySales"
      />
      <KpiCard
        :value="averageOrderValue"
        label="Gj.snitt ordre"
        :subtitle="dateSubtitle"
        icon="receipt"
        iconBgColor="#FF6B6B"
        format="currency"
        :loading="loadingTodaySales"
      />
      <KpiCard
        :value="peakHourDisplay"
        label="Topptimen"
        :subtitle="dateSubtitle"
        icon="clock"
        iconBgColor="#45B7D1"
        format="time"
        :loading="loadingHourlyChart"
      />
    </div>

    <!-- Location Overview Section (only for multi-location merchants) -->
    <div v-if="hasMultiLocations && selectedLocationId === 'ALL'" class="dashboard__section">
      <div class="dashboard__section-header">
        <h2 class="dashboard__section-title">Avdelingsoversikt</h2>
        <span class="dashboard__section-subtitle">Status og ytelse per avdeling · {{ dateSubtitle }}</span>
      </div>
      <div class="dashboard__location-grid">
        <InlineSpinner v-if="loadingLocationsOverview" />
        <template v-else>
          <LocationOverviewCard
            v-for="locData in locationsOverviewData"
            :key="locData.location.id"
            :location="locData.location"
            :salesToday="locData.salesToday"
            :salesLabel="salesLabel"
            :salesDate="isToday ? '' : salesSubtitle"
            :salesYesterday="locData.salesYesterday"
            :ordersToday="locData.ordersToday"
            :peakHour="locData.peakHour"
            :isClosed="isLocationClosed(locData.location.id)"
            @view-details="viewLocationDetails"
          />
        </template>
      </div>
    </div>

    <!-- Location Comparison Chart (only for multi-location merchants viewing all) -->
    <div v-if="hasMultiLocations && selectedLocationId === 'ALL'" class="dashboard__section">
      <InlineSpinner v-if="loadingLocationsOverview" />
      <LocationComparisonChart
        v-else
        title="Salg per avdeling"
        :subtitle="dateSubtitle"
        :locations="locationComparisonData"
      />
    </div>

    <!-- Charts Row -->
    <div class="dashboard__charts-row">
      <div class="dashboard__chart-col dashboard__chart-col--donut">
        <InlineSpinner v-if="loadingTodayChart" />
        <SalesDonutChart
          v-else
          title="Salg per ordretype"
          :subtitle="hourlyChartSubtitle"
          :labels="totalSalesTodayByTypeLabels"
          :series="totalSalesTodayByType"
        />
      </div>
      <div class="dashboard__chart-col dashboard__chart-col--hourly">
        <InlineSpinner v-if="loadingHourlyChart" />
        <HourlySalesChart
          v-else
          :title="hourlyChartTitle"
          :subtitle="hourlyChartSubtitle"
          :labels="hourlyLabels"
          :data="hourlySales"
        />
      </div>
    </div>

    <!-- Sales Trend Chart -->
    <div class="dashboard__section">
      <InlineSpinner v-if="loadingDailyChart && loadingMonthlyChart" />
      <SalesTrendChart
        v-else
        title="Salgstrend"
        :subtitle="activeTab === 0 ? 'Siste 30 dager' : 'Månedlig oversikt'"
        :labels="activeTab === 0 ? totalSalesPerDateLabels : totalSalesPerMonthLabels"
        :data="activeTab === 0 ? totalSalesPerDate : totalSalesPerMonth"
        :tabs="['Siste 30 dager', 'Månedlig']"
        :activeTab="activeTab"
        :chartType="activeTab === 0 ? 'area' : 'bar'"
        :isMonthlyView="activeTab === 1"
        @tab-change="activeTab = $event"
      />
    </div>
    </div>
  </div>
</template>

<script>
import ControlPanel from "@/components/dashboard/ControlPanel";
import InlineSpinner from "@/components/other/InlineSpinner";
import KpiCard from "@/components/dashboard/KpiCard";
import SalesDonutChart from "@/components/dashboard/SalesDonutChart";
import SalesTrendChart from "@/components/dashboard/SalesTrendChart";
import HourlySalesChart from "@/components/dashboard/HourlySalesChart";
import LocationOverviewCard from "@/components/dashboard/LocationOverviewCard";
import LocationComparisonChart from "@/components/dashboard/LocationComparisonChart";
import DashboardService from "@/services/dashboard/DashboardService";
import LocationService from "@/services/location/LocationService";
import ProductService from "@/services/product/ProductService";
import SiteThemeService from "@/services/site/SiteThemeService";
import SiteHomeService from "@/services/site/SiteHomeService";
import SubscriptionService from "@/services/site/SubscriptionService";
import LocationUtil from "@/util/LocationUtil";
import MerchantUtil from "@/util/MerchantUtil";
import UserUtil from "@/util/UserUtil";
import moment from "moment/min/moment-with-locales";
import Datepicker from '@/components/other/ToggleDatepicker.vue';
import { nb } from 'date-fns/locale';

moment.locale("nb");

export default {
  components: {
    ControlPanel,
    InlineSpinner,
    KpiCard,
    SalesDonutChart,
    SalesTrendChart,
    HourlySalesChart,
    LocationOverviewCard,
    LocationComparisonChart,
    Datepicker
  },
  data() {
    return {
      activeTab: 0,
      selectedLocationId: localStorage.getItem('dashboardLocationId') || 'ALL',
      obLoaded: false,
      obSite: false,
      obMenu: false,
      obPayment: false,
      billingLoaded: false,
      billingStatus: { status: '', trialEnd: '', hasCard: false },
      // The POS shop (device store) lives in CAGISTA, not Backoffice → an absolute link, opened in
      // a new tab. VERIFY the production CAGISTA domain below (the dev server runs on :3030).

      // Date selection
      selectedDateObj: new Date(),
      nbLocale: nb,

      // Loading states
      loadingBasicData: true,
      loadingTodaySales: true,
      loadingTodayChart: true,
      loadingDailyChart: true,
      loadingMonthlyChart: true,
      loadingHourlyChart: true,
      loadingLocationsOverview: true,

      // Sales data for selected date range
      totalSales: 0,
      totalSalesPrevious: 0,
      ordersCount: 0,
      ordersPrevious: 0,

      // Chart data
      totalSalesTodayByTypeLabels: [],
      totalSalesTodayByType: [],
      totalSalesPerDateLabels: [],
      totalSalesPerDate: [],
      totalSalesPerMonthLabels: [],
      totalSalesPerMonth: [],
      hourlyLabels: ['00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23'],
      hourlySales: [],

      // Control panel data
      closingDaysArray: [],
      locations: [],
      categories: [],
      soldOutItems: [],

      // Locations overview data
      locationsOverviewData: [],

      // For validating saved location
      pendingLocationValidation: null
    };
  },
  computed: {
    isAccountant() {
      return UserUtil.isAccountant();
    },
    /**
     * The two steps that turn a website into a shop. Only these — the homepage is stated above the
     * list, and hardware was removed: it is the one step that costs money, and a banner that will not
     * go away until the merchant buys a terminal is a sales tactic, not onboarding.
     */
    onboardingSteps() {
      // before + <a href=link>linkText</a> + after  (link omitted when null)
      return [
        { key: 'menu', before: '', linkText: 'Sett opp menyen', link: '/products', after: ' (kategorier, retter og priser)', done: this.obMenu },
        { key: 'payment', before: '', linkText: 'Aktiver betaling med VIVA', link: '/vivaOnboarding', after: ' — én betalingsløsning for både ONLINE og I BUTIKK', done: this.obPayment }
      ];
    },
    orderingStarted() {
      return this.obMenu || this.obPayment;
    },
    setupCtaLink() {
      if (!this.obSite) return '/siteBuilder';
      return this.obMenu ? '/vivaOnboarding' : '/products';
    },
    setupCtaLabel() {
      if (!this.obSite) return 'Åpne Website Builder';
      return this.obMenu ? 'Aktiver betaling' : 'Sett opp menyen';
    },
    /**
     * When to nudge at all.
     *
     * <b>Not "until everything is done".</b> The homepage is free, so finishing it is worth a nudge.
     * Online ordering is a paid upgrade: a merchant who has published their homepage and gone no
     * further has chosen the free tier, and is not behind on anything. They are nudged only once they
     * have actually STARTED on ordering — a menu or a payment method — and then only until it works.
     */
    showSiteSetupBanner() {
      if (!this.obLoaded || !MerchantUtil.useSaaSFeatures()
          || UserUtil.isAccountant() || UserUtil.isEmployee()) {
        return false;
      }
      if (!this.obSite) return true;
      return this.orderingStarted && this.onboardingSteps.some((s) => !s.done);
    },
    billingSuspended() {
      return this.billingStatus.status === 'expired' || this.billingStatus.status === 'unpaid';
    },
    billingPastDue() {
      return this.billingStatus.status === 'past_due';
    },
    trialNotStarted() {
      return this.billingLoaded && this.billingStatus.status === 'onboarding';
    },
    billingNeedsCard() {
      return this.billingStatus.status === 'trialing'; // card-free trial
    },
    showBillingBanner() {
      // SaaS-only: legacy merchants are not on the subscription engine — no trial/card nudges.
      return this.billingLoaded && MerchantUtil.useSaaSFeatures()
        && !UserUtil.isAccountant() && !UserUtil.isEmployee()
        && (this.billingSuspended || this.billingPastDue || this.billingNeedsCard);
    },
    billingBannerClass() {
      if (this.billingSuspended) return 'billing-banner--urgent';
      if (this.billingPastDue) return 'billing-banner--warn';
      return '';
    },
    billingDaysLeft() {
      if (!this.billingStatus.trialEnd) return 0;
      return Math.max(0, Math.ceil(moment(this.billingStatus.trialEnd).diff(moment()) / 86400000));
    },
    hasMultiLocations() {
      return LocationUtil.hasMultiLocations();
    },
    selectedDate() {
      return moment(this.selectedDateObj).format('YYYY-MM-DD');
    },
    todayISO() {
      return moment().format('YYYY-MM-DD');
    },
    isToday() {
      return this.selectedDate === this.todayISO;
    },
    salesLabel() {
      if (this.isToday) return 'Dagens omsetning';
      return 'Omsetning';
    },
    salesSubtitle() {
      if (this.isToday) return this.comparisonText;
      return moment(this.selectedDate).format('D. MMM YYYY');
    },
    dateSubtitle() {
      if (this.isToday) return 'I dag';
      return moment(this.selectedDate).format('D. MMM YYYY');
    },
    hourlyChartTitle() {
      return 'Salg per time';
    },
    hourlyChartSubtitle() {
      if (this.isToday) return 'I dag';
      return moment(this.selectedDate).format('D. MMM YYYY');
    },
    salesTrend() {
      if (!this.totalSalesPrevious || this.totalSalesPrevious === 0) return null;
      return Math.round(((this.totalSales - this.totalSalesPrevious) / this.totalSalesPrevious) * 100);
    },
    ordersTrend() {
      if (!this.ordersPrevious || this.ordersPrevious === 0) return null;
      return Math.round(((this.ordersCount - this.ordersPrevious) / this.ordersPrevious) * 100);
    },
    averageOrderValue() {
      if (!this.ordersCount || this.ordersCount === 0) return 0;
      return Math.round(this.totalSales / this.ordersCount);
    },
    peakHourDisplay() {
      if (!this.hourlySales || this.hourlySales.length === 0) return '--:--';
      const maxVal = Math.max(...this.hourlySales);
      if (maxVal === 0) return '--:--';
      const maxIndex = this.hourlySales.indexOf(maxVal);
      return this.hourlyLabels[maxIndex] + ':00';
    },
    comparisonText() {
      if (this.salesTrend === null) return 'Sammenligner med dagen før';
      return this.salesTrend >= 0 ? 'Opp fra dagen før' : 'Ned fra dagen før';
    },
    locationComparisonData() {
      return this.locationsOverviewData.map(locData => ({
        name: locData.location.name,
        sales: locData.salesToday || 0,
        isClosed: this.isLocationClosed(locData.location.id)
      }));
    }
  },
  watch: {
    selectedLocationId(newLocationId) {
      if (newLocationId) {
        localStorage.setItem('dashboardLocationId', newLocationId);
      }
    }
  },
  async created() {
    // Validate saved location still exists (user might have been removed from a location)
    const savedLocationId = localStorage.getItem('dashboardLocationId');
    if (savedLocationId && savedLocationId !== 'ALL') {
      // Will validate after locations are loaded
      this.pendingLocationValidation = savedLocationId;
    }

    // Priority 1: Load control panel data first for immediate interactivity
    await this.loadControlPanelDataFirst();

    // Validate saved location exists
    if (this.pendingLocationValidation) {
      const locationExists = this.locations.some(loc => loc.id === this.pendingLocationValidation);
      if (!locationExists) {
        this.selectedLocationId = 'ALL';
        localStorage.setItem('dashboardLocationId', 'ALL');
      }
    }

    // Priority 2: Load other data in parallel (non-blocking)
    this.loadOtherDataAsync();
    this.loadAnalyticsDataAsync();

    // Onboarding checklist (non-blocking)
    this.checkOnboarding();

    // Billing trial/card status (non-blocking)
    this.checkBilling();

    // Priority 3: Load locations overview for multi-location merchants
    if (this.hasMultiLocations) {
      this.loadLocationsOverviewAsync();
    }
  },
  methods: {
    async checkOnboarding() {
      // Onboarding checklist: which steps toward a live ordering site are done (evaluated on the first location).
      if (UserUtil.isAccountant() || UserUtil.isEmployee()) return;
      try {
        const locRes = await LocationService.listLocations();
        const locs = (locRes.data || []).filter((l) => l && l.shortName);
        if (locs.length) {
          const loc = locs[0];
          this.obPayment = loc.onlinePaymentProvider === 'VIVA';
          try {
            const s = await SiteThemeService.getSiteSchema(loc.shortName);
            // "Done" only once the merchant has actually customized the theme — not the untouched
            // auto-seed. A merchant save bumps version ≥ 2 and drops the `seeded` flag; a pre-seed
            // merchant who genuinely built a v1 theme has no `seeded` flag, so they still count as done.
            this.obSite = !!(s.data && s.data.schema) && (s.data.version >= 2 || !s.data.seeded);
          } catch (e) { console.log(e); }
          if (!this.obSite) {
            try {
              // Publishing the homepage counts too. This step sends them to Website Builder, which now
              // opens on the Hjemmeside tab — and a merchant who publishes there without touching the
              // colours leaves the ordering-page schema untouched (the propagation skips a location
              // already on that theme). Reading only that document would leave the banner up for
              // someone who did exactly what it told them to.
              const h = await SiteHomeService.getHome();
              // Same rule as the ordering-page schema above, and for the same reason: a merchant who
              // pre-dates the registration seed publishes their FIRST homepage as version 1, so the
              // version number alone would leave the banner up for someone who has published.
              const d = h.data || {};
              this.obSite = d.publishedVersion >= 2 || (d.publishedVersion >= 1 && !d.publishedSeeded);
            } catch (e) { console.log(e); }
          }
        }
        try {
          const cats = await ProductService.getCategories();
          const items = await ProductService.getItems();
          this.obMenu = (cats.data || []).length > 0 && (items.data || []).length > 0;
        } catch (e) { console.log(e); }
      } catch (e) {
        console.log(e);
      } finally {
        this.obLoaded = true;
      }
    },
    async checkBilling() {
      if (UserUtil.isAccountant() || UserUtil.isEmployee()) return;
      // SaaS-only, like showBillingBanner below. Gating only the banner still made the CALL, and the
      // status endpoint creates the subscription document as a side effect of being read — so every
      // legacy merchant whose admin opened this page acquired one and turned up in the onboarding funnel.
      if (!MerchantUtil.useSaaSFeatures()) return;
      try {
        const res = await SubscriptionService.getStatus();
        this.billingStatus = res.data || this.billingStatus;
      } catch (e) {
        console.log(e);
      } finally {
        this.billingLoaded = true;
      }
    },
    async loadControlPanelDataFirst() {
      try {
        const basicResponse = await DashboardService.getBasicData();
        const basicData = basicResponse.data;

        this.locations = basicData.locations;
        this.closingDaysArray = basicData.closingDaysArray;
        this.categories = basicData.categories;
        this.soldOutItems = basicData.soldOutItems;
        this.loadingBasicData = false;
      } catch (error) {
        console.error('Error loading basic data:', error);
        this.loadingBasicData = false;
      }
    },
    async loadOtherDataAsync() {
      try {
        let salesResponse;
        if (this.selectedLocationId === 'ALL') {
          salesResponse = await DashboardService.getSalesData(this.selectedDate, this.selectedDate);
        } else {
          salesResponse = await DashboardService.getSalesDataByLocation(this.selectedLocationId, this.selectedDate, this.selectedDate);
        }
        const salesData = salesResponse.data;

        this.totalSales = salesData.totalSales || 0;
        this.totalSalesPrevious = salesData.totalSalesPrevious || 0;
        this.ordersCount = salesData.ordersCount || 0;
        this.ordersPrevious = salesData.ordersPrevious || 0;
        this.totalSalesTodayByTypeLabels = salesData.salesByTypeLabels || [];
        this.totalSalesTodayByType = salesData.salesByType || [];

        // Hourly sales data
        if (salesData.hourlySales) {
          this.hourlySales = salesData.hourlySales;
        } else {
          this.hourlySales = this.hourlyLabels.map(() => 0);
        }
        if (salesData.hourlyLabels) {
          this.hourlyLabels = salesData.hourlyLabels;
        }

        this.loadingTodaySales = false;
        this.loadingTodayChart = false;
        this.loadingHourlyChart = false;
      } catch (error) {
        console.error('Error loading sales data:', error);
        this.loadingTodaySales = false;
        this.loadingTodayChart = false;
        this.loadingHourlyChart = false;
      }
    },
    async loadAnalyticsDataAsync() {
      try {
        let analyticsResponse;
        if (this.selectedLocationId === 'ALL') {
          analyticsResponse = await DashboardService.getAnalyticsData();
        } else {
          analyticsResponse = await DashboardService.getAnalyticsDataByLocation(this.selectedLocationId);
        }
        const analyticsData = analyticsResponse.data;

        this.totalSalesPerDateLabels = analyticsData.totalSalesPerDateLabels || [];
        this.totalSalesPerDate = analyticsData.totalSalesPerDate || [];
        this.loadingDailyChart = false;

        this.totalSalesPerMonthLabels = analyticsData.totalSalesPerMonthLabels || [];
        this.totalSalesPerMonth = analyticsData.totalSalesPerMonth || [];
        this.loadingMonthlyChart = false;
      } catch (error) {
        console.error('Error loading analytics data:', error);
        this.loadingDailyChart = false;
        this.loadingMonthlyChart = false;
      }
    },
    async loadLocationsOverviewAsync() {
      this.loadingLocationsOverview = true;
      try {
        const response = await DashboardService.getLocationsOverview(this.selectedDate);
        const overviewData = response.data;

        if (overviewData && overviewData.locations) {
          this.locationsOverviewData = overviewData.locations;
        } else {
          // Fallback: create overview data from existing locations
          this.locationsOverviewData = this.locations.map(loc => ({
            location: loc,
            salesToday: 0,
            salesYesterday: 0,
            ordersToday: 0,
            peakHour: '--:--'
          }));
        }
        this.loadingLocationsOverview = false;
      } catch (error) {
        console.error('Error loading locations overview:', error);
        // Fallback on error
        this.locationsOverviewData = this.locations.map(loc => ({
          location: loc,
          salesToday: 0,
          salesYesterday: 0,
          ordersToday: 0,
          peakHour: '--:--'
        }));
        this.loadingLocationsOverview = false;
      }
    },
    isLocationClosed(locationId) {
      const today = moment(new Date());
      for (let i = 0; i < this.closingDaysArray.length; i++) {
        const closingDays = this.closingDaysArray[i];
        if (closingDays.locationId == locationId) {
          const fromDate = moment(closingDays.fromDate);
          const toDate = moment(closingDays.toDate);
          if (moment(today.format('YYYY-MM-DD')).isBetween(fromDate.format('YYYY-MM-DD'), toDate.format('YYYY-MM-DD'), null, '[]')) {
            return true;
          }
        }
      }
      return false;
    },
    onLocationChange() {
      // Reset loading states
      this.loadingTodaySales = true;
      this.loadingTodayChart = true;
      this.loadingHourlyChart = true;
      this.loadingDailyChart = true;
      this.loadingMonthlyChart = true;

      // Reload data for selected location
      this.loadOtherDataAsync();
      this.loadAnalyticsDataAsync();
    },
    onDateChange() {
      // Reset loading states
      this.loadingTodaySales = true;
      this.loadingTodayChart = true;
      this.loadingHourlyChart = true;

      // Reload sales data for selected date
      this.loadOtherDataAsync();

      // Reload locations overview for multi-location merchants
      if (this.hasMultiLocations) {
        this.loadLocationsOverviewAsync();
      }
    },
    viewLocationDetails(locationId) {
      this.selectedLocationId = locationId;
      this.onLocationChange();
    }
  }
};
</script>

<style scoped>
.dashboard {
  padding: 24px;
  background-color: var(--bo-ground);
  min-height: 100vh;
}

.dashboard__content {
  max-width: 1860px;
  margin: 0 auto;
}

.site-setup-banner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  background: var(--bo-warn-bg);
  border: 1px solid var(--bo-warn-line);
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 20px;
}
.site-setup-banner__text { color: var(--bo-warn-fg); flex: 1; min-width: 280px; }
.site-setup-banner__note { margin: 10px 0 4px; }
.site-setup-banner__trial { margin: 10px 0 0; font-weight: 600; }
.site-setup-banner__steps { margin: 6px 0 0; padding-left: 0; list-style: none; }
/* override the global `li { float: left }` (navbar.css) so the steps stack vertically */
.site-setup-banner__steps li { float: none; display: flex; align-items: baseline; gap: 0.5rem; margin-bottom: 5px; }
.site-setup-step__check { display: inline-block; width: 1.1em; font-weight: 700; color: #b08900; }
.site-setup-banner__steps li.done { color: var(--bo-ok-fg); }
.site-setup-banner__steps li.done .site-setup-step__check { color: #2f9e44; }
.site-setup-banner__btn { white-space: nowrap; }

.billing-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  background: var(--bo-info-bg);
  border: 1px solid var(--bo-info-line);
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 20px;
}
.billing-banner__text { color: var(--bo-info-fg); flex: 1; min-width: 280px; }
.billing-banner--warn { background: var(--bo-warn-bg); border-color: var(--bo-warn-line); }
.billing-banner--warn .billing-banner__text { color: var(--bo-warn-fg); }
.billing-banner--urgent { background: var(--bo-bad-bg); border-color: var(--bo-bad-line); }
.billing-banner--urgent .billing-banner__text { color: var(--bo-bad-fg); }
.billing-banner__btn { white-space: nowrap; }

.dashboard__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.dashboard__header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.dashboard__title {
  font-size: 28px;
  font-weight: 700;
  color: var(--bo-ink);
  margin: 0 0 4px 0;
}

.dashboard__subtitle {
  font-size: 14px;
  color: var(--bo-muted);
  margin: 0;
}

.dashboard__location-selector {
  position: relative;
}

.dashboard__select {
  padding: 10px 36px 10px 16px;
  font-size: 14px;
  font-weight: 500;
  color: var(--bo-ink);
  background: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  min-width: 180px;
}

.dashboard__select:focus {
  outline: none;
  border-color: #B30E10;
  box-shadow: 0 0 0 3px rgba(179, 14, 16, 0.1);
}

.dashboard__date-selector {
  display: flex;
  gap: 12px;
  align-items: center;
}

.dashboard__date-input {
  /* The containing block for the calendar popout — see the .v3dp__popout rule below. */
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bo-surface);
  padding: 8px 28px 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--bo-line);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
}

.dashboard__date-input:hover {
  border-color: #B30E10;
  box-shadow: 0 2px 6px rgba(179, 14, 16, 0.15);
}

.dashboard__date-input :deep(input) {
  border: none;
  font-size: 14px;
  color: var(--bo-ink);
  background: transparent;
  cursor: pointer;
  width: 130px;
  padding: 0;
  padding-right: 20px;
  margin-right: -20px;
}

.dashboard__date-input :deep(input):focus {
  outline: none;
}

.dashboard__date-input :deep(.v3dp__datepicker) {
  --vdp-bg-color: var(--bo-surface);
  --vdp-text-color: var(--bo-ink);
  --vdp-hover-bg-color: var(--bo-surface-2);
  --vdp-selected-bg-color: #B30E10;
  --vdp-selected-color: var(--bo-surface);
}

.dashboard__date-icon {
  color: var(--bo-alert-ink);
  font-size: 14px;
}

/*
 * The calendar's right edge on the right edge of the box you can SEE.
 *
 * `right: 0` resolves against the nearest positioned ancestor, and the library makes
 * .v3dp__datepicker `position: relative` — whose right edge sits ~28px inside the field, because the
 * box carries padding-right for the chevron and the input is pulled in with a negative margin. Flush
 * to that inner edge reads as a near-miss against the border beside it.
 *
 * So the BOX becomes the containing block and the library's element steps out of the way. Nothing
 * else is positioned against it: .v3dp__clearable is `position: relative` against itself.
 */
.dashboard__date-input :deep(.v3dp__datepicker) {
  position: static;
}

/*
 * Fine pointers only. On touch the popout is fixed to the viewport instead (touch.css), and this rule
 * would otherwise win on specificity — a scoped `:deep` carries the component's attribute selector,
 * which outranks `body .v3dp__datepicker .v3dp__popout` — and quietly pin the calendar's right edge to
 * the screen edge with no margin.
 */
@media (pointer: fine) {
  .dashboard__date-input :deep(.v3dp__popout) {
    /* On the box's bottom BORDER edge — 100% reaches the inside of the 1px border, which the 1px
       clears. Without it the calendar starts inside the field's own bottom padding. */
    top: calc(100% + 1px);
    right: 0;
    left: auto;
  }
}

.dashboard__section {
  margin-bottom: 24px;
  position: relative;
  min-height: 100px;
}

.dashboard__section-header {
  margin-bottom: 16px;
}

.dashboard__section-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--bo-ink);
  margin: 0 0 4px 0;
}

.dashboard__section-subtitle {
  font-size: 13px;
  color: var(--bo-muted);
}

.dashboard__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.dashboard__location-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.dashboard__charts-row {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 20px;
  margin-bottom: 24px;
}

.dashboard__chart-col {
  min-height: 350px;
  position: relative;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .dashboard__kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .dashboard__charts-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard {
    padding: 16px;
  }

  /*
   * Title and date on one row, the location select full-width under them — laid out, not overlapped.
   *
   * The date box used to be lifted out of flow with `position: absolute; top: 0; right: 0` to put it
   * beside the title. Out of flow means nothing reserves its height, and the box is taller than the
   * heading, so it hung down into the row below and sat on top of the location select.
   *
   * A grid places it instead. The two controls are siblings inside .dashboard__header-right, so that
   * wrapper becomes `display: contents` and lets them be positioned as if they were direct children —
   * the date into the title's row, the location across the full width beneath. Everything stays in
   * flow, so the rows size themselves to whatever is tallest and cannot collide.
   */
  .dashboard__header {
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 12px;
  }

  .dashboard__header > div:first-child {
    grid-column: 1;
    grid-row: 1;
    display: flex;
    align-items: center;
  }

  .dashboard__header > div:first-child h3 {
    margin-bottom: 0;
  }

  .dashboard__header-right {
    display: contents;
  }

  .dashboard__date-selector {
    grid-column: 2;
    grid-row: 1;
    justify-self: end;
  }

  .dashboard__location-selector {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  .dashboard__location-selector {
    width: 100%;
  }

  .dashboard__select {
    width: 100%;
  }

  .dashboard__kpis {
    grid-template-columns: 1fr;
  }

  .dashboard__location-grid {
    grid-template-columns: 1fr;
  }
}

/* Override ControlPanel styles */
:deep(.controlContainer) {
  background-color: var(--bo-surface);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

:deep(.quick-button) {
  border-radius: 8px;
  transition: all 0.2s ease;
}

:deep(.quick-button:hover) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>
